import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { leaderboardStore } from '../stores/leaderboardStore'
import { gamificationStore } from '../stores/gamificationStore'
import { mojilandStore } from '../stores/mojilandStore'
import { shuffleArray } from '../utils/helpers'
import { memojiLevels } from '../utils/levels'
import { resources } from '../utils/sets'
import { playSound, vibrate } from '../utils/sounds'
import GameLeaderboard from '../components/GameLeaderboard'

interface MemojiCard {
  id: number
  icon: string
  isFlipped: boolean
  isMatched: boolean
}

type GamePhase = 'ready' | 'preview' | 'playing' | 'complete'

const PREVIEW_TIME = 4000

const Memoji = observer(() => {
  const [level, setLevel] = useState(0)
  const [cards, setCards] = useState<MemojiCard[]>([])
  const [flippedCards, setFlippedCards] = useState<number[]>([])
  const [moves, setMoves] = useState(0)
  const [matches, setMatches] = useState(0)
  const [isChecking, setIsChecking] = useState(false)
  const [phase, setPhase] = useState<GamePhase>('ready')
  const [countdown, setCountdown] = useState(3)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [resourcesEarned, setResourcesEarned] = useState<Record<string, number>>({})
  const [streak, setStreak] = useState(0)
  const [maxStreak, setMaxStreak] = useState(0)
  const [perfectGame, setPerfectGame] = useState(true)
  const [lastMatchedIds, setLastMatchedIds] = useState<number[]>([])
  const [currentScore, setCurrentScore] = useState(0)

  const totalPairs = Math.min((memojiLevels[level] || memojiLevels[0]).length, 6 + level)
  const gridCols = totalPairs <= 4 ? 4 : totalPairs <= 6 ? 4 : 4

  const sound = (type: Parameters<typeof playSound>[0]) => {
    if (soundEnabled) playSound(type)
  }

  // Crear el deck de cartas
  const createDeck = useCallback((lvl: number) => {
    const set = memojiLevels[lvl] || memojiLevels[0]
    const numPairs = Math.min(set.length, 6 + lvl)
    const pairs = set.slice(0, numPairs)
    return shuffleArray([...pairs, ...pairs]).map((icon, id) => ({
      id,
      icon,
      isFlipped: false,
      isMatched: false,
    }))
  }, [])

  // Iniciar juego
  const startGame = useCallback(() => {
    const deck = createDeck(level)
    setCards(deck.map(c => ({ ...c, isFlipped: true })))
    setFlippedCards([])
    setMoves(0)
    setMatches(0)
    setIsChecking(false)
    setPhase('preview')
    setCountdown(3)
    setResourcesEarned({})
    setStreak(0)
    setMaxStreak(0)
    setPerfectGame(true)
    setLastMatchedIds([])
    setCurrentScore(0)
    
    sound('tick')

    let count = 3
    const countInterval = setInterval(() => {
      count--
      setCountdown(count)
      if (soundEnabled) {
        if (count > 0) {
          playSound('tick')
        } else {
          playSound('go')
        }
      }
      if (count <= 0) {
        clearInterval(countInterval)
      }
    }, 1000)

    setTimeout(() => {
      setCards(deck.map(c => ({ ...c, isFlipped: false })))
      setPhase('playing')
    }, PREVIEW_TIME)
  }, [level, createDeck, soundEnabled])

  // Verificar victoria
  useEffect(() => {
    if (phase === 'playing' && matches === cards.length / 2 && cards.length > 0) {
      setPhase('complete')
      
      const baseScore = (cards.length / 2) * 20
      const movesPenalty = Math.max(0, moves - (cards.length / 2)) * 2
      const streakBonus = maxStreak * 5
      const perfectBonus = perfectGame ? 50 : 0
      const score = Math.max(10, baseScore - movesPenalty + streakBonus + perfectBonus + (level + 1) * 10)
      
      const earned: Record<string, number> = {}
      const resourceReward = Math.floor(score / 15) + level + 1
      resources.forEach(r => {
        earned[r] = Math.floor(resourceReward / resources.length) + (Math.random() > 0.5 ? 1 : 0)
      })
      setResourcesEarned(earned)
      
      Object.entries(earned).forEach(([icon, amount]) => {
        profileStore.addResource(icon, amount)
      })
      
      leaderboardStore.submitGameScore('memoji', score, level)
      gamificationStore.recordGameResult('memoji', score, level, true)
      mojilandStore.tryCompleteFromGame('memoji', score, level, true)

      sound('win')
      vibrate([100, 50, 100, 50, 200])
    }
  }, [matches, cards.length, phase, soundEnabled, moves, level, maxStreak, perfectGame])

  // Handler para click en carta
  const handleCardClick = (cardId: number) => {
    if (phase !== 'playing' || isChecking || flippedCards.length >= 2) return
    
    const card = cards.find(c => c.id === cardId)
    if (!card || card.isFlipped || card.isMatched) return

    sound('pop')
    vibrate(20)

    const newCards = cards.map(c =>
      c.id === cardId ? { ...c, isFlipped: true } : c
    )
    setCards(newCards)

    const newFlipped = [...flippedCards, cardId]
    setFlippedCards(newFlipped)

    if (newFlipped.length === 2) {
      setMoves(m => m + 1)
      setIsChecking(true)

      const [first, second] = newFlipped
      const card1 = newCards.find(c => c.id === first)!
      const card2 = newCards.find(c => c.id === second)!

      setTimeout(() => {
        if (card1.icon === card2.icon) {
          const newStreak = streak + 1
          setStreak(newStreak)
          if (newStreak > maxStreak) setMaxStreak(newStreak)
          
          if (newStreak >= 3) {
            sound('combo')
            vibrate([30, 20, 30])
          } else {
            sound('match')
            vibrate(50)
          }
          
          setLastMatchedIds([first, second])
          setTimeout(() => setLastMatchedIds([]), 500)
          
          setCards(cards =>
            cards.map(c =>
              c.id === first || c.id === second
                ? { ...c, isMatched: true }
                : c
            )
          )
          setMatches(m => m + 1)
          // Actualizar score en tiempo real
          setCurrentScore(prev => prev + 20 + (streak * 5) + ((level + 1) * 2))
        } else {
          sound('wrong')
          vibrate(100)
          setStreak(0)
          setPerfectGame(false)
          setCards(cards =>
            cards.map(c =>
              c.id === first || c.id === second
                ? { ...c, isFlipped: false }
                : c
            )
          )
        }
        setFlippedCards([])
        setIsChecking(false)
      }, 800)
    }
  }

  const handleLevelSelect = (newLevel: number) => {
    sound('click')
    setLevel(newLevel)
    setPhase('ready')
  }

  return (
    <div className="min-h-screen memoji-bg flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 game-header">
        <div className="max-w-md mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link 
                to="/"
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70 text-gray-700"
              >
                <span className="text-sm">←</span>
              </Link>
              <img src="/logo.png" alt="PlanetMoji" className="w-7 h-7 drop-shadow-lg" />
              <span className="font-title text-base text-gray-800">Memoji</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Link to="/leaderboard" className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70">
                <span>🏆</span>
              </Link>
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70"
              >
                <span>{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>
          
          {/* Game Stats */}
          {phase !== 'ready' && (
            <div className="flex justify-center items-center gap-3 mt-2">
              <div className="px-2 py-1 rounded-lg bg-white/50 border border-white/60 text-sm font-bold">
                <span>🎯</span>
                <span className="text-gray-800 ml-1">{matches}/{cards.length / 2}</span>
              </div>
              <div className="px-2 py-1 rounded-lg bg-white/50 border border-white/60 text-sm font-bold">
                <span>👆</span>
                <span className="text-gray-800 ml-1">{moves}</span>
              </div>
              {streak > 0 && (
                <div className={`px-2 py-1 rounded-lg text-sm font-bold ${
                  streak >= 3 ? 'bg-gradient-to-r from-yellow-400 to-orange-500 animate-pulse text-white' : 'bg-white/50 border border-white/60'
                }`}>
                  <span>🔥</span>
                  <span className={streak >= 3 ? 'text-white ml-1' : 'text-gray-800 ml-1'}>{streak}</span>
                </div>
              )}
              {perfectGame && moves > 0 && (
                <div className="px-2 py-1 rounded-lg bg-gradient-to-r from-purple-400 to-pink-500 text-sm font-bold text-white">
                  <span>✨ Perfect</span>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      
      <div className="flex-1 max-w-md mx-auto px-4 py-4 w-full">

        {/* Ready screen */}
        {phase === 'ready' && (
          <div className="glass-card p-6 mb-4">
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-1">Level {level + 1}</p>
              <div className="flex justify-center gap-2 mb-4">
                {(memojiLevels[level] || memojiLevels[0]).slice(0, 6).map((emoji, i) => (
                  <span key={i} className="text-3xl">{emoji}</span>
                ))}
              </div>
              <p className="text-gray-400 text-xs mb-4">
                Memorize positions in 4 seconds<br/>
                Chain matches for streak bonus!
              </p>
              <button
                onClick={startGame}
                className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-2xl font-semibold hover:scale-105 transition-transform shadow-lg shadow-purple-500/30"
              >
                🎮 Start Game
              </button>
            </div>
          </div>
        )}

        {/* Preview countdown */}
        {phase === 'preview' && (
          <div className="text-center mb-3">
            <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm rounded-full px-4 py-2 border border-white/70">
              <span className="text-gray-700 font-medium text-sm">Memorize!</span>
              <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-lg animate-pulse">
                {countdown}
              </span>
            </div>
          </div>
        )}

        {/* Game board */}
        {phase !== 'ready' && (
          <div className="glass-card p-3 mb-4">
            <div 
              className="grid gap-2"
              style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
            >
              {cards.map((card) => {
                const isRevealed = card.isFlipped || card.isMatched
                const justMatched = lastMatchedIds.includes(card.id)
                return (
                  <button
                    key={card.id}
                    onClick={() => handleCardClick(card.id)}
                    disabled={phase !== 'playing' || card.isFlipped || card.isMatched || isChecking}
                    className={`
                      aspect-square rounded-xl flex items-center justify-center
                      transition-all duration-200 ease-out
                      ${isRevealed
                        ? card.isMatched
                          ? `bg-gradient-to-br from-emerald-400/90 to-green-500/90 ${justMatched ? 'scale-110 animate-pulse' : 'scale-95'}`
                          : 'bg-white/95 shadow-lg scale-105'
                        : 'bg-gradient-to-br from-violet-500/80 to-purple-600/80 hover:from-violet-400 hover:to-purple-500 hover:scale-105 active:scale-95 cursor-pointer'
                      }
                      ${phase !== 'playing' && !isRevealed ? 'cursor-default' : ''}
                    `}
                  >
                    <span className={`text-3xl sm:text-4xl transition-all duration-200 select-none ${
                      isRevealed ? 'scale-100' : 'scale-90 opacity-80'
                    }`}>
                      {isRevealed ? card.icon : '❓'}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Level selector */}
        <div className="glass-card p-3">
          <p className="text-center text-white/40 text-xs mb-2">Select Level</p>
          <div className="flex justify-center gap-1.5 flex-wrap">
            {memojiLevels.map((set, idx) => {
              const isActive = idx === level
              return (
                <button
                  key={idx}
                  onClick={() => handleLevelSelect(idx)}
                  className={`
                    w-9 h-9 rounded-lg flex items-center justify-center text-lg
                    transition-all duration-150 cursor-pointer
                    ${isActive 
                      ? 'bg-white scale-110 shadow-md' 
                      : 'bg-white/10 hover:bg-white/25'
                    }
                  `}
                >
                  {set[0]}
                </button>
              )
            })}
          </div>
        </div>

        {/* Mini Leaderboard - Live score */}
        <div className="mt-3">
          <GameLeaderboard 
            game="memoji"
            gameName="Memoji"
            emoji="🧠"
            currentScore={currentScore}
            gradient="from-violet-500 to-purple-500"
            limit={5}
          />
        </div>

        {/* Win modal */}
        {phase === 'complete' && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl animate-bounce-in">
              <span className="text-6xl block mb-3">{perfectGame ? '🌟' : '🎉'}</span>
              <h2 className="font-title text-2xl text-purple-600 mb-1">
                {perfectGame ? 'Perfect!' : 'You Won!'}
              </h2>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 my-4">
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-purple-500">{currentScore}</p>
                  <p className="text-xs text-gray-500">Score</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-orange-500">{moves}</p>
                  <p className="text-xs text-gray-500">Moves</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-green-500">{maxStreak}</p>
                  <p className="text-xs text-gray-500">Best Streak</p>
                </div>
              </div>
              
              {/* Resources */}
              {Object.keys(resourcesEarned).length > 0 && (
                <div className="mb-4 p-3 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-2">Resources earned:</p>
                  <div className="flex justify-center gap-3">
                    {Object.entries(resourcesEarned).map(([icon, amount]) => (
                      <div key={icon} className="flex items-center gap-1">
                        <span className="text-xl">{icon}</span>
                        <span className="font-bold text-purple-600">+{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => { setPhase('ready') }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
                >
                  🔄 Retry
                </button>
                {level < memojiLevels.length - 1 && (
                  <button
                    onClick={() => { 
                      sound('levelUp')
                      setLevel(level + 1)
                      setPhase('ready') 
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold"
                  >
                    Next →
                  </button>
                )}
                <Link
                  to="/leaderboard"
                  className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl font-semibold"
                >
                  🏆
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default Memoji
